// mobile/screens/InventoryScreen.js
import React, { useEffect, useState } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, TextInput, KeyboardAvoidingView, Platform, ScrollView, Alert
} from 'react-native';
import axios from 'axios';
import PesticideList from './PesticideList';
import HistoryScreen from './HistoryScreen';
import EnterpriseHeader from '../components/EnterpriseHeader';
import { PADIPRO, RADIUS, SPACE, padiproShadow, padiproShadowMd } from '../components/padiproTheme';
import { API_BASE } from '../utils';
import { useResponsive } from '../components/responsive';

export default function InventoryScreen({ route, navigation }) {
  const token = route?.params?.token;
  const user  = route?.params?.user;
  const { contentPadding, contentWidthStyle, isTiny } = useResponsive();

  const [tab, setTab] = useState('list');     // 'list' | 'history'
  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // ✅ query untuk input (biar ngetik bebas, gak nge-lag)
  const [query, setQuery] = useState('');

  // ✅ query yang dikirim ke list (debounced biar list gak re-render berat tiap huruf)
  const [queryDebounced, setQueryDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setQueryDebounced(query), 180); // 150–250ms enak
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/seasons`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const rows = Array.isArray(r.data) ? r.data : [];
        if (!alive) return;
        setSeasons(rows);
        const active = rows.find((s) => !s.end_date) || rows[0] || null;
        setSeasonId(active?.season_id || null);
      } catch (e) {
        console.log('inventory seasons load error:', e?.message || e);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const activeSeason = seasons.find((s) => !s.end_date) || null;
  const selectedSeason = seasons.find((s) => s.season_id === seasonId) || activeSeason;
  const isActiveSeason = !!selectedSeason && selectedSeason.season_id === activeSeason?.season_id;

  const carryOverStock = () => {
    if (!token) return Alert.alert('Error', 'Token tidak ditemukan.');
    Alert.alert(
      'Bawa stok musim lalu?',
      'Sisa stok musim lalu akan dicopy sebagai stok awal musim aktif. Data musim lama dan expense tidak dihapus.',
      [
        { text: 'Batal', style: 'cancel' },
        {
          text: 'Bawa stok',
          onPress: async () => {
            try {
              const r = await axios.post(
                `${API_BASE}/pesticides/carry-over`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
              );
              setRefreshKey((x) => x + 1);
              Alert.alert('Sukses', `${r.data?.copied_count || 0} produk dibawa ke musim aktif.`);
            } catch (e) {
              Alert.alert('Error', e?.response?.data?.error || e?.message || 'Gagal membawa stok.');
            }
          },
        },
      ]
    );
  };

  const openAddStock = () => {
    navigation.navigate('AddPesticide', { token, user, seasonId });
  };

  return (
    <KeyboardAvoidingView
      style={{ flex: 1, backgroundColor: PADIPRO.bg }}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <View style={[s.header, { padding: contentPadding }]}>
        <View style={contentWidthStyle}>
          <EnterpriseHeader
            title="Inventori Sarana Produksi"
            subtitle="Pantau pupuk, pestisida, nilai stok, dan pergerakan barang harian"
          />
          <View style={s.controlsCard}>
          <View style={s.controlTopRow}>
            {/* Segmented Tabs */}
            <View style={s.segment}>
              <TouchableOpacity
                onPress={() => setTab('list')}
                style={[s.segmentBtn, isTiny && s.segmentBtnTiny, tab === 'list' && s.segmentActive]}
                activeOpacity={0.9}
              >
                <Text style={[s.segmentText, tab === 'list' && s.segmentTextActive]}>List</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => setTab('history')}
                style={[s.segmentBtn, isTiny && s.segmentBtnTiny, tab === 'history' && s.segmentActive]}
                activeOpacity={0.9}
              >
                <Text style={[s.segmentText, tab === 'history' && s.segmentTextActive]}>History</Text>
              </TouchableOpacity>
            </View>

            <View style={s.roundActions}>
              {isActiveSeason && (
                <TouchableOpacity style={s.carryRoundBtn} activeOpacity={0.9} onPress={carryOverStock}>
                  <Text style={s.carryRoundText}>↻</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity
                style={s.addRoundBtn}
                activeOpacity={0.9}
                onPress={openAddStock}
              >
                <Text style={s.addRoundText}>＋</Text>
              </TouchableOpacity>
            </View>
          </View>

          {/* Search bar tampil hanya di tab List */}
          {tab === 'list' && (
            <View style={s.searchBox}>
              <TextInput
                value={query}
                onChangeText={setQuery}
                placeholder="Cari pupuk, pestisida, atau merek..."
                placeholderTextColor={PADIPRO.mutedSoft}
                style={s.searchInput}
                returnKeyType="search"
                autoCorrect={false}
                autoCapitalize="none"
              />
            </View>
          )}

          <View style={s.seasonBlock}>
            <Text style={s.seasonLabel}>Musim Inventori</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={s.seasonRow}>
              {seasons.map((ss) => {
                const active = ss.season_id === seasonId;
                const current = !ss.end_date;
                return (
                  <TouchableOpacity
                    key={ss.season_id}
                    onPress={() => setSeasonId(ss.season_id)}
                    activeOpacity={0.9}
                    style={[s.seasonChip, active && s.seasonChipActive]}
                  >
                    <Text style={[s.seasonChipText, active && s.seasonChipTextActive]}>
                      {ss.name}{current ? ' • aktif' : ''}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          </View>
        </View>
        </View>
      </View>

      {/* Konten tab */}
      <View style={{ flex: 1 }}>
        {tab === 'list' ? (
          // ✅ kirim yang debounced agar typing smooth
          <PesticideList route={{ params: { token, user, query: queryDebounced, embedded: true, seasonId, refreshKey } }} navigation={navigation} />
        ) : (
          <HistoryScreen route={{ params: { token, user, embedded: true, seasonId, refreshKey } }} navigation={navigation} />
        )}
      </View>

      {/* FAB tambah produk inventori (tetap tersedia di kedua tab) */}
      <TouchableOpacity
        style={s.fab}
        onPress={openAddStock}
        activeOpacity={0.9}
      >
        <Text style={s.fabPlus}>＋</Text>
      </TouchableOpacity>
    </KeyboardAvoidingView>
  );
}

const s = StyleSheet.create({
  header: { padding: SPACE.md, backgroundColor: PADIPRO.bg },
  controlsCard: {
    marginTop: SPACE.sm + 2,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    borderRadius: RADIUS.xl - 2,
    backgroundColor: PADIPRO.surface,
    padding: SPACE.sm + 2,
    ...padiproShadow,
  },

  controlTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  segment: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: PADIPRO.primarySoftStrong,
    padding: 4,
    borderRadius: RADIUS.md,
    borderWidth: 1,
    borderColor: PADIPRO.border,
  },
  segmentBtn: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: RADIUS.sm,
    alignItems: 'center',
  },
  segmentBtnTiny: {
    paddingVertical: 9,
  },
  segmentActive: {
    backgroundColor: PADIPRO.surface,
    ...padiproShadow,
  },
  segmentText: { fontWeight: '700', color: PADIPRO.muted },
  segmentTextActive: { color: PADIPRO.text },

  roundActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  addRoundBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: PADIPRO.primary,
    alignItems: 'center',
    justifyContent: 'center',
    ...padiproShadowMd,
  },
  addRoundText: {
    color: '#ffffff',
    fontWeight: '900',
    fontSize: 24,
    marginTop: -2,
  },
  carryRoundBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    borderWidth: 1,
    borderColor: PADIPRO.borderStrong,
    backgroundColor: PADIPRO.primarySoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  carryRoundText: {
    color: PADIPRO.primary,
    fontWeight: '900',
    fontSize: 19,
    marginTop: -1,
  },

  searchBox: { marginTop: SPACE.sm + 2 },
  searchInput: {
    backgroundColor: PADIPRO.surfaceSunken,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    borderRadius: RADIUS.sm,
    paddingHorizontal: 12,
    paddingVertical: 12,
    color: PADIPRO.text,
    minHeight: 44,
  },
  seasonBlock: {
    marginTop: SPACE.sm + 2,
  },
  seasonLabel: {
    color: PADIPRO.muted,
    fontSize: 11,
    fontWeight: '900',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    marginBottom: 7,
  },
  seasonRow: {
    columnGap: 8,
    paddingRight: 8,
  },
  seasonChip: {
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.surfaceSunken,
    borderRadius: RADIUS.pill,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  seasonChipActive: {
    borderColor: PADIPRO.primary,
    backgroundColor: PADIPRO.primarySoft,
  },
  seasonChipText: {
    color: PADIPRO.muted,
    fontWeight: '800',
    fontSize: 12,
  },
  seasonChipTextActive: {
    color: PADIPRO.primary,
  },
  fab: {
    position: 'absolute',
    right: 16,
    bottom: 24,
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: PADIPRO.primary,
    alignItems: 'center',
    justifyContent: 'center',
    ...padiproShadowMd,
  },
  fabPlus: { color: '#fff', fontSize: 28, fontWeight: '800', marginTop: -2 },
});
